import { windowPresets } from '../WindowLevel/constants';
import { v4 as uuid } from 'uuid';
import MammographyHangingProtocols from '../mammography/mammographyHangingProtocols';

export const MODALITIES = Object.freeze({
	XR: 'XR',
	US: 'US',
	CT: 'CT',
	MR: 'MR',
	MG: 'MG',
});

const defaultToggleOptions = {
	linking: true,
	scroll: true,
	annotations: true,
	overlay: true,
	referenceLines: true,
	scoutImageOverlay: true,
	cine: false,
};

const createRules = (type, values) => ({
	type,
	values,
});

const toggleRule = (options = defaultToggleOptions) => createRules('toggle', { options });
const windowPresetsRule = options => createRules('windowPresets', options);

const presets = Object.entries(windowPresets).reduce((acc, [key, value]) => {
	const { windowWidth, windowCenter } = value;
	acc[key] = {
		preset: key,
		ww: windowWidth,
		wl: windowCenter,
	};
	return acc;
}, {});

const createMonitor = ({ viewCode, rules, prior = null, modifier = null }) => {
	const monitor = {
		id: uuid(),
		viewCode,
		modifier,
		parentId: null,
		rules,
		layout: { x: 0, y: 0 },
	};
	if (prior) {
		monitor.prior = prior;
	}
	return monitor;
};

const createStage = ({ monitors = [], layout, rules: commonRulesForStage, defaultRule, defaultStageLayout }) => ({
	monitors: monitors.map((monitor, index) => {
		const { viewCode, modifier, rules, prior } = monitor;
		return createMonitor({
			viewCode,
			rules: rules || commonRulesForStage || defaultRule,
			prior,
			modifier,
		});
	}),
	layout: layout || defaultStageLayout,
});

const patchUpHpData = ({
	name,
	modality,
	bodyPart,
	stages,
	protocolId,
	defaultStages,
	defaultRule,
	defaultStageLayout,
	...rest
}) => ({
	'Hanging Protocol Name': name,
	'Hanging Protocol Definition Sequence': [
		{
			'Item 1': {
				Modality: [modality],
				'Anatomic Region Sequence': {
					'Item 1': ['T-D3000', 'SRT', 'Chest'],
					Laterality: '',
					BodyPart: bodyPart,
					'Procedure Code Sequence': '',
					'Reason for Requested Procedure Code Sequence': '',
				},
			},
		},
	],
	Stages: defaultStages || stages.map(stage => createStage({ ...stage, defaultRule, defaultStageLayout })),
	ProtocolID: protocolId || name,
	...rest,
});

const createHangingProtocols = ({ structure, defaultRule, defaultStageLayout }) =>
	structure.map(item => {
		const { name, modality, bodyPart, stages, protocolId, ...rest } = item;
		return patchUpHpData({
			name,
			modality,
			bodyPart,
			stages,
			protocolId,
			defaultRule,
			defaultStageLayout,
			...rest,
		});
	});

// ================================= XR Hanging Protocols ======================================= //

const xrToggleOptions = {
	...defaultToggleOptions,
	annotations: false,
	referenceLines: false,
	scoutImageOverlay: false,
};

const defaultXRHPStructure = {
	structure: [
		{
			name: 'XR Chest',
			modality: 'XR',
			bodyPart: 'Chest',
			stages: [{ monitors: [{ viewCode: 'PA' }, { viewCode: 'LAT' }] }],
			protocolId: 'XR Chest',
		},
		{
			name: 'XR Chest + Prior',
			modality: 'XR',
			bodyPart: 'Chest',
			stages: [
				{
					monitors: [{ viewCode: 'PA' }, { viewCode: 'PA', prior: 1 }],
				},
				{
					monitors: [{ viewCode: 'LAT' }, { viewCode: 'LAT', prior: 1 }],
				},
			],
			protocolId: 'XR Chest + Prior',
		},
		{
			name: 'XR Abdomen',
			modality: 'XR',
			bodyPart: 'Abdomen',
			stages: [{ monitors: [{ viewCode: 'AP' }, { viewCode: 'LAT' }] }],
			protocolId: 'XR Abdomen',
		},
		{
			name: 'XR Abdomen + Prior',
			modality: 'XR',
			bodyPart: 'Abdomen',
			stages: [
				{
					monitors: [
						{ viewCode: 'PA' },
						{ viewCode: 'PA', prior: 1 },
						{ viewCode: 'LAT' },
						{ viewCode: 'LAT', prior: 1 },
					],
					layout: { x: 1, y: 1 },
				},
			],
			protocolId: 'XR Abdomen + Prior',
		},
		{
			name: 'XR Single Extremities',
			modality: 'XR',
			bodyPart: 'Extremity',
			stages: [{ monitors: [{ viewCode: 'AP' }, { viewCode: 'LAT' }] }],
			protocolId: 'XR Single Extremities',
		},
		{
			name: 'XR Extremities 2x2',
			modality: 'XR',
			bodyPart: 'Extremity',
			stages: [
				{
					monitors: [{ viewCode: 'AP' }, { viewCode: 'LAT' }, { viewCode: 'AP' }, { viewCode: 'LAT' }],
					layout: { x: 1, y: 1 },
				},
			],
			protocolId: 'XR Extremities 2x2',
		},
		{
			name: 'XR Extremities',
			modality: 'XR',
			bodyPart: 'Extremity',
			stages: [
				{
					monitors: [
						{ viewCode: 'AP' },
						{ viewCode: 'AP', prior: 1 },
						{ viewCode: 'LAT' },
						{ viewCode: 'LAT', prior: 1 },
					],
					layout: { x: 1, y: 1 },
				},
			],
			protocolId: 'XR Extremities',
		},
	],
	defaultRule: [toggleRule(xrToggleOptions)],
	defaultStageLayout: { x: 1, y: 0 },
};

// ================================= CT Hanging Protocols ======================================= //

const ctHPMonitorRules = {
	lungPreset: [windowPresetsRule(presets.lung), toggleRule(defaultToggleOptions)],
	bonePreset: [windowPresetsRule(presets.bone), toggleRule(defaultToggleOptions)],
	softTissuePreset: [windowPresetsRule(presets.softTissue), toggleRule(defaultToggleOptions)],
	default: [toggleRule(defaultToggleOptions)],
};

const defaultCTHPStructure = {
	structure: [
		{
			name: 'CT Abdo-Pelvis',
			modality: 'CT',
			bodyPart: 'Abdomen and Pelvis',
			stages: [
				{
					monitors: [
						{ viewCode: 'CT: Axial' },
						{ viewCode: 'CT: Sagittal' },
						{ viewCode: 'CT: Coronal' },
						{ viewCode: 'CT: Axial' },
					],
					layout: { x: 1, y: 1 },
				},
			],
		},
		{
			name: 'CT KUB URO',
			modality: 'CT',
			bodyPart: 'Abdomen',
			stages: [
				{
					monitors: [
						{ viewCode: 'CT: Axial' },
						{ viewCode: 'CT: Sagittal' },
						{ viewCode: 'CT: Coronal' },
						{ viewCode: 'CT: Axial' },
					],
					layout: { x: 1, y: 1 },
				},
			],
		},
		{
			name: 'CT Chest',
			modality: 'CT',
			bodyPart: 'Chest',
			stages: [
				{
					monitors: [
						{ viewCode: 'CT: Axial' },
						{ viewCode: 'CT: Sagittal' },
						{ viewCode: 'CT: Coronal' },
						{ viewCode: 'CT: Axial', rules: ctHPMonitorRules.bonePreset },
					],
					rules: ctHPMonitorRules.lungPreset,
					layout: { x: 1, y: 1 },
				},
			],
		},
		{
			name: 'CT Chest + Prior',
			modality: 'CT',
			bodyPart: 'Chest',
			stages: [
				{
					monitors: [
						{ viewCode: 'CT: Axial' },
						{ viewCode: 'CT: Axial', prior: 1 },
						{ viewCode: 'CT: Coronal' },
						{ viewCode: 'CT: Sagittal' },
					],
					rules: ctHPMonitorRules.lungPreset,
					layout: { x: 1, y: 1 },
				},
			],
		},
		{
			name: 'CT Brain',
			modality: 'CT',
			bodyPart: 'Brain',
			stages: [
				{
					monitors: [
						{ viewCode: 'CT: Axial' },
						{ viewCode: 'CT: Sagittal' },
						{ viewCode: 'CT: Coronal' },
						{ viewCode: 'CT: Any', rules: ctHPMonitorRules.bonePreset },
					],
					rules: ctHPMonitorRules.softTissuePreset,
					layout: { x: 1, y: 1 },
				},
			],
		},
		{
			name: 'CT Brain + Priors',
			modality: 'CT',
			bodyPart: 'Brain',
			stages: [
				{
					monitors: [
						{ viewCode: 'CT: Axial' },
						{ viewCode: 'CT: Sagittal' },
						{ viewCode: 'CT: Coronal' },
						{ viewCode: 'CT: Any', rules: ctHPMonitorRules.bonePreset },
						{ viewCode: 'CT: Axial', prior: 1 },
						{ viewCode: 'CT: Any', prior: 1, rules: ctHPMonitorRules.bonePreset },
					],
					rules: ctHPMonitorRules.softTissuePreset,
					layout: { x: 1, y: 2 },
				},
			],
		},
		{
			name: 'CT Ext & Spine',
			modality: 'CT',
			bodyPart: 'Extremity',
			stages: [
				{
					monitors: [
						{ viewCode: 'CT: Axial' },
						{ viewCode: 'CT: Sagittal' },
						{ viewCode: 'CT: Coronal' },
						{ viewCode: 'CT: Any', rules: ctHPMonitorRules.softTissuePreset },
					],
					layout: { x: 1, y: 1 },
				},
			],
		},
	],
	defaultRule: ctHPMonitorRules.default,
	defaultStageLayout: { x: 1, y: 1 },
};

// ================================= US Hanging Protocols ======================================= //

const usToggleOptions = {
	...defaultToggleOptions,
	linking: false,
	annotations: false,
	referenceLines: false,
	scoutImageOverlay: false,
};

const defaultUSHpStructure = {
	structure: [
		{
			name: 'USG',
			modality: 'US',
			bodyPart: '',
			stages: [
				{
					monitors: [{ viewCode: 'US', modifier: '', rules: [toggleRule(defaultToggleOptions)] }],
					layout: { x: 0, y: 0 },
				},
			],
		},
		{
			name: 'USG-Abdo-Pelvis 2x2',
			modality: 'US',
			bodyPart: 'Abdomen and Pelvis',
			stages: [
				{
					monitors: [
						{
							viewCode: 'US',
							rules: [toggleRule({ ...usToggleOptions, cadFindings: true })],
							modifier: '',
						},
						{ viewCode: 'US', modifier: '' },
						{ viewCode: 'US', modifier: '' },
						{ viewCode: 'US', modifier: '' },
					],
					layout: { x: 1, y: 1 },
				},
			],
		},
		{
			name: 'USG-Abdo-Pelvis 3X2',
			modality: 'US',
			bodyPart: 'Abdomen and Pelvis',
			stages: [
				{
					monitors: [
						{ viewCode: 'US', modifier: '' },
						{ viewCode: 'US', modifier: '' },
						{ viewCode: 'US', modifier: '' },
						{ viewCode: 'US', modifier: '' },
						{ viewCode: 'US', modifier: '' },
						{ viewCode: 'US', modifier: '' },
					],
					layout: { x: 1, y: 2 },
				},
			],
		},
	],
	defaultRule: [toggleRule(usToggleOptions)],
	defaultStageLayout: { x: 1, y: 1 },
};

// ================================= MG Hanging Protocols ======================================= //

const mgToggleOptions = {
	...defaultToggleOptions,
	cadFindings: true,
	referenceLines: false,
	scoutImageOverlay: false,
};

const mgToggleSecondOptions = {
	...defaultToggleOptions,
	scroll: false,
	cadFindings: true,
};

const mgStagesStructure = [
	[
		{ viewCode: 'MG: RCC' },
		{ viewCode: 'MG: LCC' },
		{ viewCode: 'MG: RMLO' },
		{ viewCode: 'MG: LMLO' },
		{ layout: { x: 3, y: 0 } },
	],
	[{ viewCode: 'MG: RCC' }, { viewCode: 'MG: LCC' }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: RMLO' }, { viewCode: 'MG: LMLO' }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: RCC' }, { viewCode: 'MG: RCC', modifier: 'Tomo (3D)' }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: RMLO' }, { viewCode: 'MG: RMLO', modifier: 'Tomo (3D)' }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: LCC' }, { viewCode: 'MG: LCC', modifier: 'Tomo (3D)' }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: LMLO' }, { viewCode: 'MG: LMLO', modifier: 'Tomo (3D)' }, { layout: { x: 1, y: 0 } }],
	[
		{ viewCode: 'MG: RCC', prior: 1 },
		{ viewCode: 'MG: LCC', prior: 1 },
		{ viewCode: 'MG: RMLO', prior: 1 },
		{ viewCode: 'MG: LMLO', prior: 1 },
		{ viewCode: 'MG: RCC' },
		{ viewCode: 'MG: LCC' },
		{ viewCode: 'MG: RMLO' },
		{ viewCode: 'MG: LMLO' },
		{ layout: { x: 3, y: 1 } },
	],
	[{ viewCode: 'MG: RCC' }, { viewCode: 'MG: RCC', prior: 1 }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: RMLO' }, { viewCode: 'MG: RMLO', prior: 1 }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: LCC' }, { viewCode: 'MG: LCC', prior: 1 }, { layout: { x: 1, y: 0 } }],
	[{ viewCode: 'MG: LMLO' }, { viewCode: 'MG: LMLO', prior: 1 }, { layout: { x: 1, y: 0 } }],
	[
		{ viewCode: 'MG: RCC', rules: [toggleRule(mgToggleSecondOptions)] },
		{ viewCode: 'MG: LCC', rules: [toggleRule(mgToggleSecondOptions)] },
		{ viewCode: 'MG: RMLO', rules: [toggleRule(mgToggleSecondOptions)] },
		{ viewCode: 'MG: LMLO', rules: [toggleRule(mgToggleSecondOptions)] },
		{ layout: { x: 3, y: 0 } },
	],
];

const mgStages = mgStagesStructure.map(stage => {
	const monitorData = stage.slice(0, -1);
	const { layout } = stage[stage.length - 1];
	const monitors = monitorData.map(item => {
		const { viewCode, prior, modifier = '', rules = [toggleRule(mgToggleOptions)] } = item;
		return createMonitor({ viewCode, rules, prior, modifier });
	});
	return createStage({ monitors, layout });
});

const DefaultMGHps = [
	patchUpHpData({
		name: 'MG Overview',
		modality: 'MG',
		bodyPart: '',
		defaultStages: mgStages,
		defaultRule: null,
		defaultStageLayout: null,
	}),
	patchUpHpData({
		name: 'MG Current',
		modality: 'MG',
		bodyPart: '',
		defaultStages: mgStages.slice(0, 7),
		defaultRule: null,
		defaultStageLayout: null,
	}),
	patchUpHpData({
		name: 'MG Priors',
		modality: 'MG',
		bodyPart: '',
		defaultStages: mgStages.slice(7, 13),
		defaultRule: null,
		defaultStageLayout: null,
	}),
];

// ================================= MR Hanging Protocols ======================================= //

const defaultMRHpStructure = {
	structure: [
		{
			name: 'MR Extremities',
			modality: 'MR',
			bodyPart: 'Extremity',
			stages: [
				{
					monitors: [
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Coronal' },
						{ viewCode: 'MR: Sagittal' },
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Coronal' },
						{ viewCode: 'MR: Sagittal' },
					],
					layout: { x: 1, y: 2 },
				},
			],
		},
		{
			name: 'MR Abd + Pelvis',
			modality: 'MR',
			bodyPart: 'Abdomen and Pelvis',
			stages: [
				{
					monitors: [
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Coronal' },
						{ viewCode: 'MR: Coronal' },
						{ viewCode: 'MR: Sagittal' },
					],
					layout: { x: 1, y: 2 },
				},
			],
		},
		{
			name: 'MR Spine',
			modality: 'MR',
			bodyPart: 'Abdomen and Pelvis',
			stages: [
				{
					monitors: [
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Sagittal' },
						{ viewCode: 'MR: Coronal' },
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Sagittal' },
					],
					layout: { x: 1, y: 2 },
				},
			],
			'Number of Priors Referenced': 1,
			'Compare Mode Exists': 'N',
			'Public Protocol': 'Y',
			'Hanging Protocol Description': '',
			'Hanging Protocol Level': '',
		},
		{
			name: 'MR Brain',
			modality: 'MR',
			bodyPart: 'Brain',
			stages: [
				{
					monitors: [
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Sagittal' },
						{ viewCode: 'MR: Coronal' },
						{ viewCode: 'MR: Axial' },
					],
					layout: { x: 1, y: 1 },
				},
			],
		},
		{
			name: 'MR Brain + Priors',
			modality: 'MR',
			bodyPart: 'Brain',
			stages: [
				{
					monitors: [
						{ viewCode: 'MR: Axial' },
						{ viewCode: 'MR: Sagittal' },
						{ viewCode: 'MR: Coronal' },
						{ viewCode: 'MR: Axial', prior: 1 },
					],
					layout: { x: 1, y: 1 },
				},
			],
		},
	],
	defaultRule: [toggleRule(defaultToggleOptions)],
	defaultStageLayout: { x: 0, y: 0 },
};

export const getDefaultHangingProtocols = (wonIvMgDefaultHp = false) => {
	const _defaultHangingProtocols = {
		[MODALITIES.XR]: createHangingProtocols(defaultXRHPStructure),
		[MODALITIES.MG]: wonIvMgDefaultHp ? DefaultMGHps : MammographyHangingProtocols,
		[MODALITIES.CT]: createHangingProtocols(defaultCTHPStructure),
		[MODALITIES.US]: createHangingProtocols(defaultUSHpStructure),
		[MODALITIES.MR]: createHangingProtocols(defaultMRHpStructure),
	};

	const defaultHangingProtocols = {};
	Object.keys(_defaultHangingProtocols).forEach(modality => {
		defaultHangingProtocols[modality] = _defaultHangingProtocols[modality].map(hp => ({
			...hp,
			isDefaultProtocol: true, // Mark as default protocol to differentiate from saved protocols
		}));
	});

	return defaultHangingProtocols;
};
